// Sürücü Puantajı — paylaşılan yardımcılar (formatlama, servis tipi meta, dışa aktarma).

import * as XLSX from "xlsx";

// DailySchedule legend ile uyumlu servis tipi renkleri/etiketleri.
// Etiketler i18n anahtarıdır (driverPerformance.kind.*).
export const KIND_META = {
  SHIFT_PERSONEL: { labelKey: "driverPerformance.kind.SHIFT_PERSONEL", bg: "#1976d2", border: "#0d47a1" },
  TRANSFER: { labelKey: "driverPerformance.kind.TRANSFER", bg: "#ef6c00", border: "#b53d00" },
  STANDARD_PERSONEL: { labelKey: "driverPerformance.kind.STANDARD_PERSONEL", bg: "#90a4ae", border: "#546e7a" },
  VEHICLE_ROUTE: { labelKey: "driverPerformance.kind.VEHICLE_ROUTE", bg: "#388e3c", border: "#1b5e20" },
};

export const KIND_ORDER = ["SHIFT_PERSONEL", "TRANSFER", "STANDARD_PERSONEL", "VEHICLE_ROUTE"];

/** Dakikayı "Xs Ydk" biçimine çevirir. */
export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}dk`;
  if (rem === 0) return `${h}s`;
  return `${h}s ${rem}dk`;
};

/** Km değerini yerelleştirilmiş biçimde döner. */
export const formatKm = (km) => {
  const v = Number(km) || 0;
  return `${v.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} km`;
};

export const formatNumber = (n) => (Number(n) || 0).toLocaleString("tr-TR");

/** "YYYY-MM-DD" → "DD.MM" kısa biçim. */
export const shortDate = (ymd) => {
  if (!ymd) return "";
  const [, mo, d] = ymd.split("-");
  return `${d}.${mo}`;
};

/** "YYYY-MM-DD" → "MM-DD" (grafik ekseni etiketi). */
export const axisDate = (ymd) => {
  if (!ymd) return "";
  const [, mo, d] = ymd.split("-");
  return `${mo}-${d}`;
};

/** "YYYY-MM-DD" → "DD.MM.YYYY". */
export const longDate = (ymd) => {
  if (!ymd) return "";
  const [y, mo, d] = ymd.split("-");
  return `${d}.${mo}.${y}`;
};

const TR_WEEKDAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

export const weekdayName = (ymd) => {
  if (!ymd) return "";
  const [y, mo, d] = ymd.split("-").map((n) => parseInt(n, 10));
  return TR_WEEKDAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()];
};

export const addDays = (ymd, delta) => {
  if (!ymd) return ymd;
  const [y, mo, d] = ymd.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, mo - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

export const todayYMD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const currentMonth = () => todayYMD().slice(0, 7);

/** Dahil gün sayısı (YYYY-MM-DD aralığı). */
export const daysInRange = (rangeStart, rangeEnd) => {
  if (!rangeStart || !rangeEnd) return 30;
  const [y1, m1, d1] = rangeStart.split("-").map((n) => parseInt(n, 10));
  const [y2, m2, d2] = rangeEnd.split("-").map((n) => parseInt(n, 10));
  const startMs = Date.UTC(y1, m1 - 1, d1);
  const endMs = Date.UTC(y2, m2 - 1, d2);
  if (endMs < startMs) return 1;
  return Math.round((endMs - startMs) / 86400000) + 1;
};

/**
 * Sürücü performans tablosunu Excel (.xlsx) olarak indirir.
 * @param {Array} rows - filtrelenmiş görüntüleme satırları
 * @param {Function} t - i18n
 * @param {{ rangeStart?: string, rangeEnd?: string }} options
 */
export const exportPerformanceExcel = (rows, t, { rangeStart, rangeEnd, periodDays } = {}) => {
  if (!rows.length) return;

  const days = periodDays ?? 30;
  const ex = (k) => t(`driverPerformance.csv.${k}`);
  const exPeriod = (k) => t(`driverPerformance.csv.${k}`, { days });

  const data = rows.map((r) => ({
    [ex("driver")]: r.driver.name,
    [ex("phone")]: r.driver.phonenumber,
    [ex("active")]: r.driver.isActive ? t("driverPerformance.yes") : t("driverPerformance.no"),
    [ex("dayCount")]: r.metrics.day.count,
    [ex("dayKm")]: r.metrics.day.km,
    [ex("dayMin")]: r.metrics.day.minutes,
    [ex("weekCount")]: r.metrics.week.count,
    [ex("weekKm")]: r.metrics.week.km,
    [ex("weekMin")]: r.metrics.week.minutes,
    [exPeriod("monthCount")]: r.metrics.month.count,
    [exPeriod("monthKm")]: r.metrics.month.km,
    [exPeriod("monthMin")]: r.metrics.month.minutes,
    [t("driverPerformance.metricsMissing")]: r.metricsMissing ? t("driverPerformance.yes") : t("driverPerformance.no"),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);

  const MAX_COLUMN_WIDTH = 40;
  const columns = Object.keys(data[0]);
  worksheet["!cols"] = columns.map((col) => {
    const maxLength = Math.max(...data.map((row) => (row[col] ? row[col].toString().length : 0)), col.length);
    return { wch: Math.min(maxLength + 2, MAX_COLUMN_WIDTH) };
  });

  const workbook = XLSX.utils.book_new();
  const sheetLabel = rangeStart && rangeEnd ? `${rangeStart} - ${rangeEnd}` : todayYMD();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetLabel.slice(0, 31));

  const fileSuffix = rangeStart && rangeEnd ? `${rangeStart}_${rangeEnd}` : todayYMD();
  XLSX.writeFile(workbook, `surucu-puantaji-${fileSuffix}.xlsx`);
};

/** @deprecated CSV yerine exportPerformanceExcel kullanın */
export const exportPerformanceCsv = (rows, t) => {
  const header = [
    t("driverPerformance.csv.driver"),
    t("driverPerformance.csv.phone"),
    t("driverPerformance.csv.active"),
    t("driverPerformance.csv.dayCount"),
    t("driverPerformance.csv.dayKm"),
    t("driverPerformance.csv.dayMin"),
    t("driverPerformance.csv.weekCount"),
    t("driverPerformance.csv.weekKm"),
    t("driverPerformance.csv.weekMin"),
    t("driverPerformance.csv.monthCount"),
    t("driverPerformance.csv.monthKm"),
    t("driverPerformance.csv.monthMin"),
  ];

  const lines = rows.map((r) =>
    [
      r.driver.name,
      r.driver.phonenumber,
      r.driver.isActive ? t("driverPerformance.yes") : t("driverPerformance.no"),
      r.metrics.day.count,
      r.metrics.day.km,
      r.metrics.day.minutes,
      r.metrics.week.count,
      r.metrics.week.km,
      r.metrics.week.minutes,
      r.metrics.month.count,
      r.metrics.month.km,
      r.metrics.month.minutes,
    ].map(csvCell),
  );

  const csv = [header.map(csvCell).join(","), ...lines.map((l) => l.join(","))].join("\r\n");
  // Excel'in UTF-8'i doğru çözmesi için BOM ekle
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `surucu-puantaji-${todayYMD()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function csvCell(value) {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, "\"\"")}"`;
  return s;
}
